import { useState, useMemo, useCallback, useRef, useEffect, type JSX } from 'react';
import { createPortal } from 'react-dom';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  type CellStyle,
  type ColDef,
  type ICellRendererParams,
  type ValueGetterParams,
} from 'ag-grid-community';
import { X, Minus, Plus, ChevronDown, AlertTriangle, Check, ExternalLink } from 'lucide-react';
import clsx from 'clsx';
// Day 76 P5.1 Phase 9B — PATH_LABEL retired; labels derived from
// useSettingsConfig().activePaths (fpc.short_label).
import { useSettingsConfig } from '../../hooks/useSettingsConfig';
import type { PathProfit } from '../../types/purchasing';
import { ProvenanceTooltip } from '../ui/ProvenanceTooltip';
import type { ProvenanceMap } from '../ui/ProvenanceIndicator';
import { checkLineQty, lineQtyMessage } from '../../lib/poConstraints';

ModuleRegistry.registerModules([AllCommunityModule]);

interface FulfillmentOption {
  value: string;
  label: string;
}

function fmt$(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ── Inline fulfillment dropdown for AG Grid cells ─────────────

interface FulfillmentDropdownProps {
  value: string;
  onChange: (value: string) => void;
}

function FulfillmentDropdown({ value, onChange }: FulfillmentDropdownProps): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  // Day 75 (D4) — derived from useSettingsConfig().activePaths so dropship
  // surfaces automatically when toggled is_active=true. Day 76 P5.1 Phase 9B
  // — PATH_LABEL retired; labels derive from fpc.short_label || display_name.
  const { activePaths } = useSettingsConfig();
  const fulfillmentOptions = useMemo<FulfillmentOption[]>(
    () =>
      activePaths.map((p) => ({
        value: p.path_key,
        label: p.short_label || p.display_name,
      })),
    [activePaths]
  );
  const selected = fulfillmentOptions.find((o) => o.value === value);

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setPos({ top: rect.bottom + 2, left: rect.left });
    setOpen(true);
  };

  const handleSelect = (val: string, e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    onChange(val);
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 4,
          fontSize: 11,
          padding: '2px 6px',
          borderRadius: 4,
          width: '100%',
          background: 'var(--surface-3)',
          border: `1px solid ${open ? 'var(--accent)' : 'var(--border)'}`,
          color: 'var(--ink-secondary)',
          outline: 'none',
          textAlign: 'left',
        }}
      >
        <span>{selected?.label || value}</span>
        <ChevronDown
          size={10}
          style={{
            color: 'var(--ink-muted)',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 150ms',
          }}
        />
      </button>
      {open &&
        createPortal(
          <>
            {/* Backdrop to capture outside clicks — keyboard dismissal handled
                by browser default (Escape blurs the focused trigger). */}
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
              }}
              aria-hidden="true"
            />
            <div
              style={{
                position: 'fixed',
                top: pos.top,
                left: pos.left,
                zIndex: 9999,
                minWidth: 100,
                background: 'var(--surface-2)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                overflow: 'hidden',
              }}
            >
              {fulfillmentOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={(e) => handleSelect(opt.value, e)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '5px 12px',
                    fontSize: 11,
                    border: 'none',
                    outline: 'none',
                    background: opt.value === value ? 'rgba(124,106,247,0.15)' : 'transparent',
                    color: opt.value === value ? 'var(--accent)' : 'var(--ink-primary)',
                    transition: 'background 100ms',
                  }}
                  onMouseEnter={(e) => {
                    if (opt.value !== value) e.currentTarget.style.background = 'var(--surface-3)';
                  }}
                  onMouseLeave={(e) => {
                    if (opt.value !== value) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </>,
          document.body
        )}
    </>
  );
}

// ── Allocation popup editor ─────────────────────────────────────────────────
// Portal-rendered popover used by the Allocation cell. Auto-saves on every
// stepper click (calls onChange with the full next allocation map). The
// parent component is the source of truth — this popover only renders from
// props and mutates via the callback.

interface AllocationPopoverProps {
  qtyOrdered: number;
  allocation: Record<string, number>;
  onChange: (next: Record<string, number>) => void;
}

function AllocationPopover({
  qtyOrdered,
  allocation,
  onChange,
}: AllocationPopoverProps): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  // Local draft state so rapid bumps accumulate instantly without waiting
  // for the parent to re-render and feed new `allocation` props back in.
  // Committed to the parent via onChange on each edit so the PO Summary
  // updates live; reseeded from the prop whenever the popover is (re)opened.
  const [draft, setDraft] = useState<Record<string, number>>(() => ({ ...allocation }));

  // Day 75 (D4 + D5) — editor iterates active paths so dropship surfaces
  // when is_active=true; summary iterates allocation keys directly so a
  // path deactivated AFTER allocation was set still displays its units
  // gracefully (Sol historical-data carve-out per Day 60b precedent).
  const { activePaths, activePathKeys } = useSettingsConfig();
  // Day 76 P5.1 Phase 9B — label lookup from fpc.short_label, replacing
  // PATH_LABEL hardcoded const. Falls back to path_key for historical-data
  // carve-out paths (deactivated/legacy keys not in activePaths).
  const pathLabel = useCallback(
    (path: string): string => activePaths.find((p) => p.path_key === path)?.short_label ?? path,
    [activePaths]
  );

  const summary = useMemo(() => {
    const parts = Object.entries(allocation)
      .filter(([, v]) => (v || 0) > 0)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([p, v]) => `${pathLabel(p)} ×${v}`);
    return parts.join(' · ');
  }, [allocation, pathLabel]);

  const allocSum = useMemo(
    () => Object.values(allocation).reduce((s, v) => s + (Number(v) || 0), 0),
    [allocation]
  );
  const mismatch = allocSum !== qtyOrdered;
  const draftSum = useMemo(
    () => Object.values(draft).reduce((s, v) => s + (Number(v) || 0), 0),
    [draft]
  );
  const draftMismatch = draftSum !== qtyOrdered;

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    // Anchor the popover directly beneath the cell. If it would overflow the
    // viewport we let the browser handle scrolling — the popover is small
    // enough that this is rare at typical screen sizes.
    setPos({ top: rect.bottom + 4, left: rect.left });
    // Reseed the draft from the latest committed allocation whenever the
    // popover is (re)opened, so users don't pick up a stale draft from a
    // previous edit session.
    setDraft({ ...allocation });
    setOpen(true);
  };

  const bump = (path: string, delta: number): void => {
    setDraft((prev) => {
      const current = Number(prev[path]) || 0;
      const next = Math.max(0, current + delta);
      const copy: Record<string, number> = { ...prev };
      if (next > 0) copy[path] = next;
      else delete copy[path];
      // Commit immediately so PO Summary / dirty state update live.
      onChange(copy);
      return copy;
    });
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className={clsx(
          'w-full text-left flex items-center justify-between gap-1 rounded px-1.5 py-0.5 text-2xs transition-colors',
          mismatch
            ? 'border border-warning/50 bg-warning/10 text-warning'
            : 'bg-surface-3 border border-border text-ink-secondary hover:text-ink-primary'
        )}
        style={{ fontSize: 11 }}
        title={
          mismatch
            ? `Allocated ${allocSum} of ${qtyOrdered} — adjust so the total matches`
            : 'Edit per-path allocation'
        }
      >
        {mismatch && <AlertTriangle size={10} className="flex-shrink-0" />}
        <span className="truncate flex-1">{summary || '(unallocated)'}</span>
        <ChevronDown
          size={10}
          className="flex-shrink-0"
          style={{
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 150ms',
          }}
        />
      </button>
      {open &&
        createPortal(
          <>
            {/* Backdrop — click-outside closes the popover. No keyboard dismiss
                listener beyond browser Escape on the focused trigger. */}
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
              }}
              aria-hidden="true"
            />
            <div
              role="dialog"
              aria-label="Edit allocation"
              style={{
                position: 'fixed',
                top: pos.top,
                left: pos.left,
                zIndex: 9999,
                minWidth: 240,
                background: 'var(--surface-2)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                padding: 8,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--ink-muted)',
                  padding: '2px 6px 6px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span>Allocate {qtyOrdered} units</span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    color: draftMismatch ? 'var(--warning)' : 'var(--success)',
                  }}
                >
                  {draftSum} / {qtyOrdered}
                </span>
              </div>
              {activePathKeys.map((path) => {
                const val = Number(draft[path]) || 0;
                return (
                  <div
                    key={path}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '3px 6px',
                      borderRadius: 4,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 11,
                        color: 'var(--ink-primary)',
                        fontWeight: val > 0 ? 500 : 400,
                      }}
                    >
                      {pathLabel(path)}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          bump(path, -1);
                        }}
                        disabled={val === 0}
                        aria-label={`Decrease ${pathLabel(path)} allocation`}
                        style={{
                          width: 20,
                          height: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 4,
                          background: 'var(--surface-4)',
                          border: '1px solid var(--border)',
                          color: 'var(--ink-secondary)',
                          opacity: val === 0 ? 0.4 : 1,
                          cursor: val === 0 ? 'default' : 'pointer',
                        }}
                      >
                        <Minus size={10} />
                      </button>
                      <span
                        style={{
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: 12,
                          width: 28,
                          textAlign: 'center',
                          color: val > 0 ? 'var(--ink-bright)' : 'var(--ink-muted)',
                        }}
                      >
                        {val}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          bump(path, 1);
                        }}
                        aria-label={`Increase ${pathLabel(path)} allocation`}
                        style={{
                          width: 20,
                          height: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 4,
                          background: 'var(--surface-4)',
                          border: '1px solid var(--border)',
                          color: 'var(--ink-secondary)',
                          cursor: 'pointer',
                        }}
                      >
                        <Plus size={10} />
                      </button>
                    </div>
                  </div>
                );
              })}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  paddingTop: 6,
                  borderTop: '1px solid var(--border-subtle)',
                  marginTop: 4,
                }}
              >
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(false);
                  }}
                  style={{
                    fontSize: 11,
                    padding: '2px 8px',
                    background: 'var(--accent)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
}

// ── Line item + props ───────────────────────────────────────────────────────

interface POLineItemRow {
  id: string;
  isg_sku: string | null;
  product_id: string;
  quantity: number | null;
  cost_per_unit: number | null;
  fulfillment_path: string | null;
  products?: {
    title?: string | null;
    brand?: string | null;
    // U10 A1 — junction-assembled upstream (usePOStaging fetch: primary
    // product_distributors row → cost_usd; slice-B drop family).
    cost_usd?: number | null;
    // W4a — the ASIN deep-link + the match-status column (✓ reviewed / ⚠ resolved-unreviewed;
    // legacy match_resolved_at-NULL rows render neutral — exempt, not a warning).
    asin?: string | null;
    match_reviewed?: boolean | null;
    match_resolved_at?: string | null;
  } | null;
}

interface POLineItemsGridProps {
  items: POLineItemRow[];
  profitData?: Record<string, Record<string, PathProfit | undefined> | undefined>;
  defaultFulfillmentPath?: string;
  /** Per-staging-item per-path unit allocation. Keyed by staging row id →
   *  { [path]: quantity }. When omitted the grid falls back to showing the
   *  legacy single-path dropdown so older consumers keep working. */
  allocations?: Record<string, Record<string, number>>;
  /** Called when the user edits a row's allocation via the cell popover. */
  onAllocationChange?: (stagingId: string, next: Record<string, number>) => void;
  onUpdateQuantity: (id: string, qty: number) => void;
  onUpdateCost: (id: string, cost: number) => void;
  onUpdateFulfillmentPath: (id: string, path: string) => void;
  onRemove: (id: string) => void;
  /** Day 98 H.18/H.19 — per-line quantity limits. NULL = no limit. When set, a
   *  violating Qty cell renders a danger indicator + tooltip (hard block; the
   *  parent POBuilder.validate() gates submission on the same check). */
  maxUnitsPerSku?: number | null;
  minOrderQty?: number | null;
}

export default function POLineItemsGrid({
  items,
  profitData,
  defaultFulfillmentPath,
  allocations,
  onAllocationChange,
  onUpdateQuantity,
  onUpdateCost,
  onUpdateFulfillmentPath,
  onRemove,
  maxUnitsPerSku,
  minOrderQty,
}: POLineItemsGridProps): JSX.Element {
  const gridTheme = useMemo(
    () =>
      themeQuartz.withParams({
        backgroundColor: 'var(--surface-1)',
        foregroundColor: 'var(--ink-primary)',
        headerBackgroundColor: 'var(--surface-2)',
        headerTextColor: 'var(--ink-secondary)',
        borderColor: 'var(--border-subtle)',
        rowHoverColor: 'var(--surface-3)',
        selectedRowBackgroundColor: 'var(--accent-muted)',
        oddRowBackgroundColor: 'transparent',
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '12px',
        headerFontSize: '11px',
        headerFontWeight: '600',
        cellHorizontalPadding: '12px',
        rowBorder: { color: 'var(--border-subtle)', width: 1, style: 'solid' },
      }),
    []
  );

  // ── Custom cell renderers ─────────────────────────────────

  type RowCellProps = ICellRendererParams<POLineItemRow>;

  const SkuCell = useCallback((p: RowCellProps) => {
    if (!p.data) return null;
    return (
      <span
        style={{ fontFamily: "'JetBrains Mono', monospace", color: 'var(--accent)', fontSize: 12 }}
      >
        {p.data.isg_sku}
      </span>
    );
  }, []);

  const QuantityCell = useCallback(
    (p: RowCellProps) => {
      if (!p.data) return null;
      const qty = p.data.quantity || 1;
      const violation = checkLineQty(qty, maxUnitsPerSku ?? null, minOrderQty ?? null);
      const violationMsg = lineQtyMessage(violation, maxUnitsPerSku ?? null, minOrderQty ?? null);
      return (
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 4 }}
          title={violationMsg ?? undefined}
        >
          {violation && (
            <AlertTriangle
              size={11}
              style={{ color: 'var(--danger)', flexShrink: 0 }}
              aria-label={violationMsg ?? 'Quantity out of range'}
            />
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onUpdateQuantity(p.data!.id, Math.max(1, qty - 1));
            }}
            aria-label="Decrease quantity"
            style={{
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 4,
              background: 'var(--surface-4)',
              border: '1px solid var(--border)',
              color: 'var(--ink-secondary)',
              fontSize: 12,
              lineHeight: 1,
            }}
          >
            <Minus size={10} />
          </button>
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 12,
              width: 32,
              textAlign: 'center',
              color: violation ? 'var(--danger)' : 'var(--ink-bright)',
            }}
          >
            {qty}
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onUpdateQuantity(p.data!.id, qty + 1);
            }}
            aria-label="Increase quantity"
            style={{
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 4,
              background: 'var(--surface-4)',
              border: '1px solid var(--border)',
              color: 'var(--ink-secondary)',
              fontSize: 12,
              lineHeight: 1,
            }}
          >
            <Plus size={10} />
          </button>
        </div>
      );
    },
    [onUpdateQuantity, maxUnitsPerSku, minOrderQty]
  );

  const CostCell = useCallback(
    (p: RowCellProps) => {
      if (!p.data) return null;
      const cost = Number(p.data.cost_per_unit) || Number(p.data.products?.cost_usd) || 0;
      return (
        <input
          type="text"
          inputMode="decimal"
          value={cost || ''}
          onChange={(e) => {
            const v = e.target.value.replace(/[^0-9.]/g, '');
            onUpdateCost(p.data!.id, parseFloat(v) || 0);
          }}
          onClick={(e) => e.stopPropagation()}
          aria-label="Cost per unit"
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 12,
            width: 72,
            padding: '2px 6px',
            borderRadius: 4,
            background: 'var(--surface-3)',
            border: '1px solid var(--border)',
            color: 'var(--ink-bright)',
            outline: 'none',
          }}
        />
      );
    },
    [onUpdateCost]
  );

  // W4a — ASIN deep-link (compare the staged supplier product against the live Amazon listing
  // before committing the buy). Blank when the product has no asin (fuzzy-pending / no-listing).
  const AsinCell = useCallback((p: RowCellProps) => {
    const asin = p.data?.products?.asin;
    if (!asin) return <span style={{ color: 'var(--ink-muted)', fontSize: 12 }}>—</span>;
    return (
      <a
        href={`https://www.amazon.com/dp/${asin}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        title="Open the Amazon listing"
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 12,
          color: 'var(--accent)',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        {asin}
        <ExternalLink size={10} style={{ flexShrink: 0 }} />
      </a>
    );
  }, []);

  // W4a — match-verification status (Moment 2 gate signal). Three states, mirroring the
  // submit_purchase_order predicate: resolved+reviewed → ✓ (success); resolved+unreviewed
  // (match_reviewed === false) → ⚠ (warning — this line blocks submit until acked); legacy
  // (match_resolved_at NULL) → neutral blank (exempt — NOT a warning).
  const MatchStatusCell = useCallback((p: RowCellProps) => {
    const prod = p.data?.products;
    if (!prod || prod.match_resolved_at == null) {
      return (
        <span
          style={{ color: 'var(--ink-muted)', fontSize: 12 }}
          title="No in-house resolver match (exempt)"
        >
          —
        </span>
      );
    }
    if (prod.match_reviewed === true) {
      return (
        <span title="Match verified" style={{ display: 'inline-flex', color: 'var(--success)' }}>
          <Check size={14} />
        </span>
      );
    }
    if (prod.match_reviewed === false) {
      return (
        <span
          title="Resolved match needs verification before submit"
          style={{ display: 'inline-flex', color: 'var(--warning)' }}
        >
          <AlertTriangle size={13} />
        </span>
      );
    }
    return <span style={{ color: 'var(--ink-muted)', fontSize: 12 }}>—</span>;
  }, []);

  // New per-row Allocation cell. Shows a compact summary like
  // "FBA ×3 · FBM ×5 · Shopify ×2" and opens the AllocationPopover on click.
  //
  // The cell callback reads allocations + onAllocationChange via refs so its
  // identity stays stable across parent re-renders. Without this, every
  // `setAllocations` call in POBuilder would re-create the cellRenderer
  // function, AG Grid would rebuild the cell DOM, and the popover's local
  // `open` state would reset after every stepper click — making editing
  // impossible.
  const allocationsRef = useRef(allocations);
  const onAllocationChangeRef = useRef(onAllocationChange);
  const defaultFulfillmentPathRef = useRef(defaultFulfillmentPath);
  const onUpdateFulfillmentPathRef = useRef(onUpdateFulfillmentPath);
  // gridRef lets us force a cell refresh after the parent allocations state
  // changes — AG Grid otherwise caches the last rendered cell and wouldn't
  // know to re-invoke the stable AllocationCell renderer.
  const gridRef = useRef<AgGridReact<POLineItemRow> | null>(null);
  useEffect(() => {
    allocationsRef.current = allocations;
    // Refresh just the Allocation column so the on-cell summary stays in
    // sync with the parent-held allocations state. Force flush any
    // equality-short-circuits by passing { force: true }.
    gridRef.current?.api?.refreshCells({ force: true, columns: ['Allocation'] });
  }, [allocations]);
  useEffect(() => {
    onAllocationChangeRef.current = onAllocationChange;
  }, [onAllocationChange]);
  useEffect(() => {
    defaultFulfillmentPathRef.current = defaultFulfillmentPath;
  }, [defaultFulfillmentPath]);
  useEffect(() => {
    onUpdateFulfillmentPathRef.current = onUpdateFulfillmentPath;
  }, [onUpdateFulfillmentPath]);

  const AllocationCell = useCallback((p: RowCellProps) => {
    if (!p.data) return null;
    const currentAllocations = allocationsRef.current;
    const currentOnChange = onAllocationChangeRef.current;
    if (!currentAllocations || !currentOnChange) {
      return (
        <FulfillmentDropdown
          value={p.data.fulfillment_path || defaultFulfillmentPathRef.current || 'amazon_fba'}
          onChange={(val) => onUpdateFulfillmentPathRef.current(p.data!.id, val)}
        />
      );
    }
    const alloc = currentAllocations[p.data.id] || {};
    const qty = p.data.quantity || 1;
    return (
      <AllocationPopover
        qtyOrdered={qty}
        allocation={alloc}
        onChange={(next) => currentOnChange(p.data!.id, next)}
      />
    );
  }, []);

  // Day 84.8b P2 — Est. Profit/Unit cellRenderer wraps with sale_price_source
  // ProvenanceTooltip per F2 cross-domain consumer fold-in. profit_per_unit's
  // dominant source-flag attribution is sale_price_source (sale_price COALESCE
  // chain drives profit value; velocity_source drives returns-rate timing
  // separately). Synthetic envelope per Day 84.8a #DBN-β engine-flag-only
  // pattern; staleThresholdDays = 90 default per Restock + BuyingSession
  // precedent (synthetic envelope carries no set_at, so threshold is
  // structurally inert — kept for API symmetry). Current-state surface;
  // valueIsSnapshot defaults to false (POLineItemsGrid reads live
  // product_profitability cache via POBuilder L568 SELECT projection).
  const ProfitCell = useCallback(
    (p: RowCellProps) => {
      if (!p.data) return null;
      const path = p.data.fulfillment_path || defaultFulfillmentPath || 'amazon_fba';
      const profEntry = profitData?.[p.data.product_id]?.[path];
      const profit = profEntry?.profit_per_unit;
      if (profit === null || profit === undefined) {
        return (
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 12,
              color: 'var(--ink-muted)',
            }}
          >
            —
          </span>
        );
      }
      const color = Number(profit) > 0 ? 'var(--success)' : 'var(--danger)';
      const salePriceSource = profEntry?.sale_price_source;
      const baseContent = (
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color }}>
          {fmt$(profit)}
        </span>
      );
      if (!salePriceSource) return baseContent;
      const syntheticProv: ProvenanceMap = {
        profit_per_unit: { source: salePriceSource },
      };
      return (
        <ProvenanceTooltip
          provenance={syntheticProv}
          fieldKey="profit_per_unit"
          staleThresholdDays={90}
          mode="cell"
        >
          {baseContent}
        </ProvenanceTooltip>
      );
    },
    [profitData, defaultFulfillmentPath]
  );

  const RemoveCell = useCallback(
    (p: RowCellProps) => {
      if (!p.data) return null;
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove(p.data!.id);
          }}
          aria-label="Remove line item"
          style={{
            width: 22,
            height: 22,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 4,
            color: 'var(--ink-muted)',
            transition: 'color 150ms',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--danger)')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--ink-muted)')}
        >
          <X size={14} />
        </button>
      );
    },
    [onRemove]
  );

  const columnDefs = useMemo<ColDef<POLineItemRow>[]>(
    () => [
      // W4a — leading match-status gutter (icon only). Centered per status-badge convention.
      {
        headerName: '',
        colId: 'matchStatus',
        minWidth: 44,
        maxWidth: 44,
        width: 44,
        cellRenderer: MatchStatusCell,
        cellStyle: {
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        } as CellStyle,
        sortable: false,
        filter: false,
        headerTooltip:
          'Match verification status — ✓ verified · ⚠ needs verification · blank = exempt',
      },
      { headerName: 'ISG SKU', field: 'isg_sku', minWidth: 120, width: 120, cellRenderer: SkuCell },
      {
        headerName: 'Title',
        flex: 1,
        minWidth: 200,
        valueGetter: (p: ValueGetterParams<POLineItemRow>) => p.data?.products?.title || '—',
        cellStyle: {
          color: 'var(--ink-bright)',
          fontWeight: 500,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        } as CellStyle,
      },
      // W4a — ASIN deep-link, grouped with Title (what-is-this / which-Amazon-listing).
      { headerName: 'ASIN', colId: 'asin', minWidth: 116, width: 116, cellRenderer: AsinCell },
      {
        headerName: 'Brand',
        minWidth: 100,
        width: 100,
        valueGetter: (p: ValueGetterParams<POLineItemRow>) => p.data?.products?.brand || '—',
        cellStyle: { color: 'var(--ink-secondary)' } as CellStyle,
      },
      { headerName: 'Cost/Unit', minWidth: 100, width: 100, cellRenderer: CostCell },
      { headerName: 'Qty', minWidth: 80, width: 80, cellRenderer: QuantityCell },
      {
        headerName: 'Allocation',
        colId: 'Allocation',
        minWidth: 180,
        width: 180,
        cellRenderer: AllocationCell,
        headerTooltip:
          'Split this line item across fulfillment paths. Click to edit per-path quantities — the total must match the row Qty.',
      },
      { headerName: 'Est. Profit/Unit', minWidth: 120, width: 120, cellRenderer: ProfitCell },
      // W4a (Rule 16) — per-line "Total Cost" + "Est. Total Profit" (each = an adjacent column ×
      // Qty) dropped to fund the ASIN + match-status columns; both aggregates remain in the PO
      // Summary card + the submit-confirm modal.
      {
        headerName: '',
        minWidth: 50,
        maxWidth: 50,
        width: 50,
        cellRenderer: RemoveCell,
        sortable: false,
        filter: false,
      },
    ],
    [
      MatchStatusCell,
      SkuCell,
      AsinCell,
      CostCell,
      QuantityCell,
      AllocationCell,
      ProfitCell,
      RemoveCell,
    ]
  );

  const defaultColDef = useMemo<ColDef<POLineItemRow>>(
    () => ({
      sortable: true,
      resizable: true,
      suppressHeaderMenuButton: true,
      headerClass: 'ag-left-aligned-header',
      cellStyle: { textAlign: 'left' },
    }),
    []
  );

  return (
    <div
      className="w-full"
      style={{ height: Math.min(400, Math.max(180, items.length * 42 + 44)) }}
    >
      <AgGridReact<POLineItemRow>
        ref={gridRef}
        theme={gridTheme}
        rowData={items}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        getRowId={(p) => p.data.id}
        headerHeight={36}
        rowHeight={42}
        animateRows={false}
        suppressCellFocus={true}
        suppressHorizontalScroll={true}
        domLayout={items.length <= 8 ? 'autoHeight' : 'normal'}
        overlayNoRowsTemplate='<span style="color:var(--ink-muted);font-size:13px">No items staged. Add products from the Buying Session or use the button below.</span>'
      />
    </div>
  );
}
